// About: what the loaded tune IS — a Wikipedia paragraph on the tune and on its
// writers (only where a curated manifest verified the article; see
// data/tuneKnowledge.ts) — and where it sits in the repertoire: composer peers,
// its exact-changes family and its harmonic neighbours across the corpus. It
// rides the transport's drawer (an "About" panel beside Tempo / Repeats /
// Style), so reading it never scrolls the music away: the score stays put and
// playback keeps running behind the drawer. Mounting IS the request — the panel
// only renders while open — so the corpus loads on open rather than on every
// Workbench visit.
//
// Deliberately no stats strip (user ruling July 2026): key and style already
// print on the masthead, bar and chord counts on the transport's own chips, and
// the density/vocabulary/ii–V numbers belong to the Corpus page's analytics.
// This panel answers "what else should I play", not "measure this chart".

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  hoistLifespan,
  shortenExtract,
  splitDescription,
  type TuneRecording,
  type WikipediaSummary,
} from '@/data/tuneKnowledge';
import { buildProseIndex, linkifyProse, type ProseIndex } from '@/data/proseLinks';
import { useCorpusRepository } from '@/data/useCorpusRepository';
import { useTuneKnowledge, type TuneKnowledge } from '@/data/useTuneKnowledge';
import { tuneFacts, type TuneFacts } from '@/music/corpus';
import { tuneMap } from '@/music/tuneMap';
import type { IRealChart } from '@/music/ireal/types';
import {
  normalizeTuneText,
  buildTuneRelations,
  type HarmonicNeighbour,
} from '@/music/tuneRelations';
import { openPalette } from '@/components/paletteBus';
import { tuneHref } from '@/components/tuneHref';
import { BUNDLED_SCORES } from '@/data/bundledScores';
import { buildMelodyLookup, type MelodyLookup } from '@/data/melodyVersions';
import { openbookLibrary, scoreHasMelody } from '@/data/openbookLibrary';
import { realbookLibrary } from '@/data/realbookLibrary';
import { useStore } from '@/state/store';

const tuneUrl = (tune: Pick<TuneFacts, 'title' | 'composer'>, source?: 'realbook') =>
  tuneHref(tune.title, tune.composer, source);

/**
 * Which relation rows open a tune with a written melody (#419). The corpus
 * manifests arrive asynchronously, so until they land the rows simply carry
 * no badge; they never claim a melody the link would not open.
 */
function useMelodyLookup(): MelodyLookup {
  const savedCharts = useStore((s) => s.savedCharts);
  const [corpora, setCorpora] = useState<{
    openbook: { title: string; composer?: string }[];
    realbook: { title: string; composer?: string }[];
  }>({ openbook: [], realbook: [] });
  useEffect(() => {
    let live = true;
    void Promise.all([openbookLibrary().catch(() => []), realbookLibrary()]).then(
      ([openbook, realbook]) => {
        if (live) setCorpora({ openbook, realbook });
      },
    );
    return () => {
      live = false;
    };
  }, []);
  return useMemo(
    () =>
      buildMelodyLookup({
        saved: savedCharts.map((score) => ({
          title: score.title,
          composer: score.composer,
          melody: scoreHasMelody(score),
        })),
        bundled: BUNDLED_SCORES.map((entry) => entry.summary),
        openbook: corpora.openbook,
        realbook: corpora.realbook,
      }),
    [savedCharts, corpora],
  );
}

/** The ♪ the Tunes browser wears, for a row whose link opens a melody. */
function MelodyBadge() {
  return (
    <span className="tune-melody" title="Has a written melody" aria-label="has melody">
      {'♪'}
    </span>
  );
}

function TuneLinks({
  title,
  hint,
  tunes,
  total,
  melody,
  onNavigate,
}: {
  title: string;
  hint: string;
  tunes: TuneFacts[];
  /** The library's full count, so a truncated list admits what it is hiding. */
  total: number;
  melody: MelodyLookup;
  onNavigate?: () => void;
}) {
  if (tunes.length === 0) return null;
  return (
    <section className="tune-about-group">
      <h3>
        {title}
        <small>
          {hint}
          {total > tunes.length ? ` · ${tunes.length} of ${total}` : ''}
        </small>
      </h3>
      <ul className="tune-about-list">
        {tunes.map((tune) => {
          const version = melody(tune.title, tune.composer);
          return (
            <li key={`${tune.title}-${tune.composer}`}>
              <Link to={tuneUrl(tune, version?.source)} onClick={onNavigate}>
                <strong>
                  {tune.title}
                  {version && (
                    <>
                      {' '}
                      <MelodyBadge />
                    </>
                  )}
                </strong>
                {tune.composer && <span>{tune.composer}</span>}
              </Link>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function NeighbourLinks({
  neighbours,
  melody,
  onNavigate,
}: {
  neighbours: HarmonicNeighbour[];
  melody: MelodyLookup;
  onNavigate?: () => void;
}) {
  if (neighbours.length === 0) return null;
  return (
    <section className="tune-about-group">
      <h3>Harmonic neighbours</h3>
      <ul className="tune-about-list">
        {neighbours.map(({ tune, reason }) => {
          const version = melody(tune.title, tune.composer);
          return (
            <li key={`${tune.title}-${tune.composer}`}>
              <Link to={tuneUrl(tune, version?.source)} onClick={onNavigate}>
                <strong>
                  {tune.title}
                  {version && (
                    <>
                      {' '}
                      <MelodyBadge />
                    </>
                  )}
                </strong>
                <span>{reason}</span>
              </Link>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/**
 * One article: its lead image where there is one, then the paragraph. The image
 * is decorative here (`alt=""`) because the heading beside it already names the
 * subject, and it links on to the article, which carries the picture's own
 * credit — so a reader is never more than one click from the source.
 */
/**
 * The paragraph, with the two things the app can honour turned into ways in:
 * a tune we hold opens it, a person the library credits searches for theirs.
 * `openPalette` rather than a route because there are no artist pages — the
 * search IS the artist page, and it already indexes composer credits.
 */
function ProseText({
  text,
  index,
  skip,
  onNavigate,
}: {
  text: string;
  index: ProseIndex | null;
  skip: string[];
  onNavigate?: () => void;
}) {
  const segments = useMemo(
    () => (index ? linkifyProse(text, index, skip) : [{ kind: 'text' as const, text }]),
    [text, index, skip],
  );
  return (
    <p>
      {segments.map((segment, at) => {
        if (segment.kind === 'text') return segment.text;
        if (segment.kind === 'tune') {
          return (
            <Link key={at} to={tuneUrl(segment)} onClick={onNavigate} className="tune-about-ref">
              {segment.text}
            </Link>
          );
        }
        return (
          <button
            key={at}
            type="button"
            className="tune-about-ref"
            title={`Search the library for ${segment.name}`}
            onClick={() => openPalette(segment.name)}
          >
            {segment.text}
          </button>
        );
      })}
    </p>
  );
}

/**
 * Who to listen to — the article's own catalogue, rendered as rows. An artist
 * the library credits opens the palette search (same rule as prose person
 * links); everyone else is plain text, because there are no artist pages and a
 * dead link would be worse than a name.
 */
function Recordings({ rows, index }: { rows: TuneRecording[]; index: ProseIndex | null }) {
  if (rows.length === 0) return null;
  return (
    <ul className="tune-about-recordings" data-testid="tune-about-recordings">
      {rows.map((row) => {
        const credit = index?.people.get(normalizeTuneText(row.artist));
        return (
          <li key={`${row.artist}|${row.work ?? ''}|${row.year ?? ''}`}>
            {credit ? (
              <button
                type="button"
                className="tune-about-ref"
                title={`Search the library for ${credit}`}
                onClick={() => openPalette(credit)}
              >
                {row.artist}
              </button>
            ) : (
              <span className="tune-about-rec-artist">{row.artist}</span>
            )}
            {row.work && <span className="tune-about-rec-work">{row.work}</span>}
            {row.year !== undefined && <span className="tune-about-rec-year">{row.year}</span>}
          </li>
        );
      })}
    </ul>
  );
}

function Article({
  summary,
  sentences,
  index,
  skip,
  recordings,
  onNavigate,
}: {
  summary: WikipediaSummary;
  /** The tune gets more room than its writers: it is what the reader opened. */
  sentences: number;
  index: ProseIndex | null;
  skip: string[];
  recordings?: TuneRecording[];
  onNavigate?: () => void;
}) {
  const { role, dateline: dates } = splitDescription(summary.description);
  const { dateline: fromLead, extract } = hoistLifespan(summary.extract);
  const body = extract.trim() ? shortenExtract(extract, sentences) : '';
  // The one-liner usually restates the first sentence ("American jazz
  // trumpeter"); it only earns its line when it says something new. The dates
  // survive that test on their own — they are never in the paragraph.
  const identity =
    role && body && !body.toLowerCase().includes(role.toLowerCase()) ? role : body ? null : role;
  const subtitle = [dates ?? fromLead, identity].filter(Boolean).join(' · ');
  return (
    <section className={`tune-about-article${summary.image ? ' has-thumb' : ''}`}>
      {summary.image && (
        <img
          className="tune-about-thumb"
          src={summary.image.source}
          alt=""
          loading="lazy"
          referrerPolicy="no-referrer"
        />
      )}
      <h3>{summary.title}</h3>
      {subtitle && <p className="tune-about-dateline">{subtitle}</p>}
      {body && <ProseText text={body} index={index} skip={skip} onNavigate={onNavigate} />}
      {recordings && recordings.length > 0 && <Recordings rows={recordings} index={index} />}
      <a href={summary.url} target="_blank" rel="noreferrer noopener">
        Read on Wikipedia
      </a>
    </section>
  );
}

function Prose({
  knowledge,
  index,
  skip,
  onNavigate,
}: {
  knowledge: TuneKnowledge;
  index: ProseIndex | null;
  skip: string[];
  onNavigate?: () => void;
}) {
  const { tune, composers, recordings } = knowledge;
  if (!tune && composers.length === 0) return null;
  return (
    <div className="tune-about-prose tune-about-region" data-testid="tune-about-prose">
      {/* The cards are their own grid so the licence line below can't span it:
          a spanning item occupies every track, and an occupied track is never
          empty, so auto-fit had nothing to collapse and two cards sat in the
          left two thirds of a wide drawer. */}
      <div className="tune-about-cards">
        {/* The tune leads: the writers explain who made the thing you opened. */}
        {tune && (
          <Article
            summary={tune}
            sentences={3}
            index={index}
            skip={skip}
            recordings={recordings}
            onNavigate={onNavigate}
          />
        )}
        {composers.map((writer) => (
          <Article
            key={writer.url}
            summary={writer}
            sentences={2}
            index={index}
            // A writer's own name is the heading above the paragraph.
            skip={[...skip, writer.title]}
            onNavigate={onNavigate}
          />
        ))}
      </div>
      {/* CC BY-SA requires naming the source and its licence wherever the text
          is reused, which a summary in a drawer still is. */}
      <p className="tune-about-credit">
        Summaries from{' '}
        <a href="https://en.wikipedia.org" target="_blank" rel="noreferrer noopener">
          Wikipedia
        </a>
        , licensed{' '}
        <a
          href="https://creativecommons.org/licenses/by-sa/4.0/"
          target="_blank"
          rel="noreferrer noopener"
        >
          CC BY-SA 4.0
        </a>
        .
      </p>
    </div>
  );
}

/** `onNavigate` lets the host close its drawer when a related tune is opened. */
export function TuneAbout({ chart, onNavigate }: { chart: IRealChart; onNavigate?: () => void }) {
  const current = useMemo(() => tuneFacts(chart), [chart]);
  // The tune map's one line: keys, devices, bars and the surprise per bar
  // (lower is easier to remember), the same line the Map mode prints.
  const map = useMemo(() => tuneMap(chart), [chart]);
  const { repository, error } = useCorpusRepository();
  const { knowledge } = useTuneKnowledge(current.title, current.composer);
  const melody = useMelodyLookup();
  const relations = useMemo(
    // Eight rows a list, not the default four: the relations column scrolls on
    // its own now, so a long list costs nothing but its own scrollbar, and the
    // header still says "8 of 30" when the library holds more.
    () => (repository ? buildTuneRelations(current, repository.stats.facts, 8) : null),
    [current, repository],
  );
  // The prose can only link into what the library holds, so the index comes
  // from the same corpus the relations do — it arrives with them, and until
  // then the paragraphs simply read as text.
  const proseIndex = useMemo(
    () => (repository ? buildProseIndex(repository.stats.facts) : null),
    [repository],
  );
  // What the panel already answers: the open tune (you are on it) and its
  // writers (their cards are right here, their catalogue is the list below).
  const proseSkip = useMemo(
    () => [current.title, ...knowledge.composers.map((writer) => writer.title)],
    [current.title, knowledge.composers],
  );
  const nothingRelated =
    relations !== null &&
    relations.moreByComposer.length === 0 &&
    relations.sharedChanges.length === 0 &&
    relations.harmonicNeighbours.length === 0;

  return (
    <div
      className="panel tune-about"
      data-testid="tune-about"
      data-relations-ready={relations ? 'true' : 'false'}
    >
      <Prose knowledge={knowledge} index={proseIndex} skip={proseSkip} onNavigate={onNavigate} />
      <p className="tune-about-map" data-testid="tune-about-map">
        <strong>Tune map</strong> {map.summary}
      </p>
      {relations ? (
        <div className="tune-about-groups tune-about-region" data-testid="tune-about-relations">
          <TuneLinks
            title="More by this composer"
            hint={current.composer || 'Unknown writer'}
            tunes={relations.moreByComposer}
            total={relations.moreByComposerTotal}
            melody={melody}
            onNavigate={onNavigate}
          />
          <TuneLinks
            title="Shared changes"
            hint="The same progression, note for note"
            tunes={relations.sharedChanges}
            total={relations.sharedChangesTotal}
            melody={melody}
            onNavigate={onNavigate}
          />
          <NeighbourLinks
            neighbours={relations.harmonicNeighbours}
            melody={melody}
            onNavigate={onNavigate}
          />
          {nothingRelated && (
            <p className="panel-hint" data-testid="tune-about-empty">
              Nothing in the library shares this tune's changes — it stands on its own.
            </p>
          )}
        </div>
      ) : (
        <div className="tune-about-region">
          <p className="panel-hint" data-testid="tune-about-status">
            {error ? `Related tunes unavailable — ${error}` : 'Reading the library…'}
          </p>
        </div>
      )}
    </div>
  );
}
